import React from 'react';

export const PAGE_SIZE = 10;

export function paginateItems(items, page, pageSize = PAGE_SIZE) {
  const list = Array.isArray(items) ? items : [];
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const currentPage = Math.min(Math.max(1, page || 1), totalPages);
  const startIndex = total === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageItems = list.slice(startIndex, startIndex + pageSize);
  const start = total === 0 ? 0 : startIndex + 1;
  const end = total === 0 ? 0 : startIndex + pageItems.length;

  return { pageItems, totalPages, start, end, total, currentPage };
}

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set([1, totalPages, currentPage]);
  for (let offset = 1; offset <= 2; offset += 1) {
    if (currentPage - offset > 1) pages.add(currentPage - offset);
    if (currentPage + offset < totalPages) pages.add(currentPage + offset);
  }

  const sorted = [...pages].sort((a, b) => a - b);
  const withEllipsis = [];
  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) {
      withEllipsis.push('ellipsis');
    }
    withEllipsis.push(page);
  });
  return withEllipsis;
}

function Pagination({
  page,
  pageSize = PAGE_SIZE,
  total,
  onPageChange,
  className = '',
}) {
  const { start, end, totalPages, currentPage } = paginateItems(
    Array.from({ length: total || 0 }),
    page,
    pageSize
  );
  const pageNumbers = getPageNumbers(currentPage, totalPages);
  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages || total === 0;

  const goTo = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) return;
    onPageChange(nextPage);
  };

  return (
    <div
      className={`mt-4 w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${className}`}
      data-pagination="true"
    >
      <p className="text-sm text-gray-700">
        Showing <span className="font-semibold text-gray-900">{start}</span> to{' '}
        <span className="font-semibold text-gray-900">{end}</span> of{' '}
        <span className="font-semibold text-gray-900">{total}</span> results
      </p>
      <nav className="inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
        <button
          type="button"
          onClick={() => goTo(currentPage - 1)}
          disabled={isFirst}
          className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        {pageNumbers.map((item, index) =>
          item === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500"
            >
              ...
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => goTo(item)}
              aria-current={item === currentPage ? 'page' : undefined}
              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                item === currentPage
                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {item}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => goTo(currentPage + 1)}
          disabled={isLast}
          className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </nav>
    </div>
  );
}

export default Pagination;
